import { useQuery } from "@tanstack/react-query";
import api from "../services/api";

interface FeeData {
  total_collected: number;
  recent_fees: Array<{
    id: string;
    amount: string;
    balance_after: string;
    reference_id: string;
    created_at: string;
  }>;
}

export default function AdminFeesPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "fees"],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: FeeData }>("/admin/fees");
      return response.data.data;
    },
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Platform Fees</h1>
        <p className="text-gray-400 mt-2">Track platform fee collection (20% of survey budgets)</p>
      </div>

      {/* Summary Card */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-400 text-sm font-medium">Total Fees Collected</p>
            <p className="text-4xl font-bold text-white mt-2">
              ${data?.total_collected.toFixed(2) || "0.00"}
            </p>
          </div>
          <div className="w-16 h-16 rounded-lg bg-purple-500/20 flex items-center justify-center text-3xl">
            💎
          </div>
        </div>
      </div>

      {/* Recent Fees */}
      <div className="card overflow-hidden">
        <h2 className="text-xl font-bold text-white mb-4">Recent Fee Transactions</h2>

        {isLoading ? (
          <div className="text-center py-12 text-gray-400">Loading fees...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-dark-border">
                  <th className="text-left py-4 px-6 text-gray-400 font-medium">Amount</th>
                  <th className="text-left py-4 px-6 text-gray-400 font-medium">Balance After</th>
                  <th className="text-left py-4 px-6 text-gray-400 font-medium">Reference</th>
                  <th className="text-left py-4 px-6 text-gray-400 font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {data?.recent_fees.map((fee) => (
                  <tr key={fee.id} className="border-b border-dark-border hover:bg-dark-700">
                    <td className="py-4 px-6">
                      <span className="text-green-400 font-medium">
                        +${parseFloat(fee.amount).toFixed(2)}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-white">
                      ${parseFloat(fee.balance_after).toFixed(2)}
                    </td>
                    <td className="py-4 px-6 text-gray-400">
                      <span className="text-xs">{fee.reference_id.slice(0, 12)}...</span>
                    </td>
                    <td className="py-4 px-6 text-gray-400">
                      {new Date(fee.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {!data?.recent_fees.length && (
              <div className="text-center py-12 text-gray-400">No fee transactions yet</div>
            )}
          </div>
        )}
      </div>

      {/* Info Card */}
      <div className="card bg-blue-500/10 border border-blue-500/20">
        <h3 className="text-lg font-bold text-blue-400 mb-2">💡 How Platform Fees Work</h3>
        <ul className="space-y-2 text-gray-300 text-sm">
          <li>• Platform takes a 20% fee from all survey budgets</li>
          <li>• Fees are collected when surveys are funded</li>
          <li>• Remaining 80% is used to reward responders</li>
          <li>• Fees help maintain and improve the platform</li>
        </ul>
      </div>
    </div>
  );
}
